import { useState } from "react";
import { Bell, X, Zap, TrendingUp, BarChart2, Truck, Megaphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useUnseenSpotlights, useMarkSpotlightSeen } from "@/hooks/useSpotlightReads";
import type { MarketSpotlight } from "@/hooks/useMarketSpotlights";

const EVENT_ICON: Record<string, React.ElementType> = {
  cme_move: TrendingUp,
  regional_spread: BarChart2,
  dv_volume: Zap,
  diesel_spike: Truck,
};

const EVENT_LABEL: Record<string, string> = {
  cme_move: "CME Move",
  regional_spread: "Regional Spread",
  dv_volume: "Volume Alert",
  diesel_spike: "Diesel Spike",
};

function SpotlightItem({
  spotlight,
  onMarkSeen,
}: {
  spotlight: MarketSpotlight;
  onMarkSeen: (id: string) => void;
}) {
  const isManual = spotlight.source === "manual" || !spotlight.event_type;
  const Icon = isManual ? Megaphone : (EVENT_ICON[spotlight.event_type!] ?? Megaphone);
  const label = isManual ? "Market Update" : (EVENT_LABEL[spotlight.event_type!] ?? "Alert");

  return (
    <div className="flex items-start gap-3 py-3 border-b border-border last:border-0">
      <Icon className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
            {label}
          </span>
          {spotlight.region && (
            <span className="text-[10px] text-muted-foreground">· {spotlight.region}</span>
          )}
        </div>
        <p className="text-sm font-medium text-foreground leading-snug">{spotlight.headline}</p>
        <p className="text-xs text-muted-foreground mt-0.5 leading-snug line-clamp-2">{spotlight.body}</p>
        {spotlight.cta_url && (
          <a
            href={spotlight.cta_url}
            className="inline-block mt-1 text-xs font-medium text-primary hover:underline"
          >
            {spotlight.cta_text} →
          </a>
        )}
      </div>
      <button
        onClick={() => onMarkSeen(spotlight.id)}
        className="shrink-0 text-muted-foreground hover:text-foreground transition-colors mt-0.5"
        aria-label="Mark as read"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export function SpotlightNotificationBell() {
  const [open, setOpen] = useState(false);
  const { data: unseen = [], isLoading } = useUnseenSpotlights();
  const markSeen = useMarkSpotlightSeen();

  const count = unseen.length;

  function handleMarkSeen(id: string) {
    markSeen.mutate(id);
  }

  function handleMarkAllSeen() {
    unseen.forEach((s) => markSeen.mutate(s.id));
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-8 w-8 text-muted-foreground hover:text-foreground"
          aria-label={count > 0 ? `${count} market alert${count > 1 ? "s" : ""}` : "Market alerts"}
        >
          <Bell className="h-4 w-4" />
          {!isLoading && count > 0 && (
            <span className="absolute top-1 right-1 flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-destructive" />
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-80 p-0" sideOffset={8}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <Bell className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-sm font-semibold">Market Alerts</span>
            {count > 0 && (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
                {count}
              </Badge>
            )}
          </div>
          {count > 1 && (
            <button
              onClick={handleMarkAllSeen}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Mark all read
            </button>
          )}
        </div>

        {/* Body */}
        <div className="px-4 max-h-[360px] overflow-y-auto">
          {isLoading ? (
            <p className="text-sm text-muted-foreground text-center py-6">Loading…</p>
          ) : count === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 gap-2 text-center">
              <Bell className="h-6 w-6 text-muted-foreground/30" />
              <p className="text-sm text-muted-foreground">You're all caught up</p>
            </div>
          ) : (
            unseen.map((s) => (
              <SpotlightItem key={s.id} spotlight={s} onMarkSeen={handleMarkSeen} />
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
